import React from 'react'
import { alpha, useTheme } from '@mui/material/styles'
import {
  Box,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useMediaQuery,
  IconButton,
} from '@mui/material'
import {
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material'

import { ContentItem } from '../../types/catalog'
import { StatusBadge } from './ContentCard'
import MediaPlaceholder from './MediaPreview'

interface ContentCardPreviewProps {
  item: ContentItem
  open: boolean
  onClose: (e?: React.MouseEvent) => void
  onEdit: (item: ContentItem) => void
}

const ContentCardPreview: React.FC<ContentCardPreviewProps> = ({ item, open, onClose, onEdit }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  
  const isUploaded = item.type !== 'external_video' && (!!item.media_id || item.has_integration)
  const status = item.status
  const hasMediaPreview = (isUploaded && (!!item.preview_url || item.type === 'document')) || !isUploaded;

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose(e);
    onEdit(item);
  }

  // Typecast href to string, since undefined throws a type error in MUI Button
  const resourceUrl = item.preview_url || item.file_url || '';

  return (
    <Dialog
      open={open}
      fullScreen={fullScreen}
      onClose={(e: any) => {
        if (e && e.stopPropagation) e.stopPropagation();
        onClose(e);
      }}
      onClick={(e) => e.stopPropagation()}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: fullScreen ? 0 : 3, 
          overflow: 'hidden',
          position: 'relative'
        }
      }}
    >
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          onClose(e);
        }}
        size="small"
        sx={{
          position: 'absolute',
          top: 20,
          right: 22,
          zIndex: 10,
          bgcolor: (theme) => alpha(theme.palette.background.paper, 0.8),
          backdropFilter: 'blur(4px)',
          transition: 'all 0.2s ease',
          '&:hover': {
            bgcolor: 'background.paper',
            transform: 'scale(1.1)'
          }
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      {hasMediaPreview && (
        <Box sx={{ width: '100%', pt: '60%', position: 'relative', bgcolor: (isUploaded && item.type !== 'document') ? 'black' : 'action.hover' }}>
          {isUploaded ? (
            item.type === 'video' ? (
              <Box
                component="video"
                src={item.media_url || item.preview_url}
                controls
                autoPlay
                sx={{
                  position: 'absolute',
                  top: 0, left: 0,
                  width: '100%', height: '100%',
                  objectFit: 'contain'
                }}
              />
            ) : item.type === 'document' ? (
              <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', overflow: 'hidden', bgcolor: 'grey.100' }}>
                <Box sx={{
                  position: 'absolute',
                  bottom: 12, right: 12,
                  zIndex: 2,
                }}>
                  <IconButton 
                    component="a"
                    href={resourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    sx={{ 
                      bgcolor: 'rgba(255,255,255,0.9)',
                      color: 'text.primary',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      backdropFilter: 'blur(8px)',
                      p: 1.2,
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      mr: 0.8,
                      '&:hover': {
                        bgcolor: 'white',
                        transform: 'scale(1.08) translateY(-2px)',
                        boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                      }
                    }}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box
                  component="iframe"
                  src={`${item.file_url || item.media_url || ''}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                  title={item.title}
                  scrolling="no"
                  sx={{
                    position: 'absolute',
                    top: 0, left: 0,
                    width: 'calc(100% + 24px)',
                    height: 'calc(100% + 24px)',
                    border: 'none',
                  }}
                />
              </Box>
            ) : (
              <Box
                component="img"
                src={item.preview_url || item.file_url || ''}
                alt={item.title}
                sx={{
                  position: 'absolute',
                  top: 0, left: 0,
                  width: '100%', height: '100%',
                  objectFit: 'contain'
                }}
              />
            )
          ) : (
            <MediaPlaceholder item={item} isUploaded={isUploaded} size="large" />
          )}
        </Box>
      )}
      <DialogTitle sx={{ pb: 1, pt: 3, pr: hasMediaPreview ? 3 : 8 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, gap: 2 }}>
          <Typography variant="h5" fontWeight="700" sx={{ textTransform: 'capitalize', lineHeight: 1.2 }}>
            {item.title || 'Untitled'}
          </Typography>
          <Box sx={{ flexShrink: 0, mt:-0.2}}>
            <StatusBadge status={status} />
          </Box>
        </Box>
        {item.tags && item.tags.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            {item.tags.map((tag, idx) => (
              <Chip key={idx} label={tag} size="small" sx={{ 
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                color: 'primary.main', border: 'none', fontWeight: 500, borderRadius: 1
              }} />
            ))}
          </Box>
        )}
      </DialogTitle>
      <DialogContent sx={{ 
        pb: 3,
        '&::-webkit-scrollbar': { width: '6px' },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.15)', borderRadius: '10px' },
        '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(0,0,0,0.3)' }
      }}>

        
        <Typography variant="body1" sx={{ 
          whiteSpace: 'pre-wrap', 
          wordBreak: 'break-word',
          color: 'text.secondary',
          lineHeight: 1.7,
        }}>
          {item.description || 'No description provided.'}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 2, justifyContent: 'space-between', alignItems: 'center' }}>
        <Button 
          onClick={onClose}
          disableElevation
          sx={{ 
            color: 'text.primary',
            bgcolor: (theme) => alpha(theme.palette.text.primary, 0.05),
            '&:hover': {
              bgcolor: (theme) => alpha(theme.palette.text.primary, 0.1),
            },
            borderRadius: 2, 
            px: 3 
          }}
        >
          Close
        </Button>
        <Button 
          variant="contained" 
          disableElevation
          onClick={handleEditClick}
          sx={{ borderRadius: 2, px: 3 }}
        >
          Edit Item
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ContentCardPreview
